import jwt from 'jsonwebtoken'
import crypto from 'crypto'
import type { AuthConfig } from './auth.types.js'

export interface JwtPayload {
  userId: string
  email: string
  roleIds: string[]    // Multi-role support (IDs for DB lookups)
  roleNames: string[]  // Role names for ability building (ADMIN, EDITOR, etc.)
  impersonatedBy?: string  // Admin user ID that started the impersonation
}

export interface TokenPair {
  accessToken: string
  refreshToken: string
}

/** Claims that onTokenCreate hooks cannot overwrite */
export const SYSTEM_CLAIMS = new Set([
  'userId', 'email', 'roleIds', 'roleNames', 'impersonatedBy',
  'iat', 'exp', 'iss', 'sub', 'aud', 'nbf', 'jti'
])

function parseExpiration(exp: string): number {
  const match = exp.match(/^(\d+)([smhd])$/)
  if (!match) return 900 // 15 min default

  const value = parseInt(match[1]!, 10)
  const unit = match[2]

  switch (unit) {
    case 's': return value
    case 'm': return value * 60
    case 'h': return value * 60 * 60
    case 'd': return value * 60 * 60 * 24
    default: return 900
  }
}

export async function generateAccessToken(
  payload: JwtPayload,
  config: AuthConfig,
  user?: Record<string, unknown>
): Promise<string> {
  const finalPayload: Record<string, unknown> = { ...payload }

  if (config.onTokenCreate) {
    const customClaims = await config.onTokenCreate(payload, user)
    for (const [key, value] of Object.entries(customClaims)) {
      if (!SYSTEM_CLAIMS.has(key)) {
        finalPayload[key] = value
      }
    }
  }

  return jwt.sign(finalPayload, config.secret, {
    expiresIn: config.accessExpires as jwt.SignOptions['expiresIn']
  })
}

export function generateRefreshToken(): string {
  return crypto.randomBytes(64).toString('hex')
}

export function getRefreshTokenExpiration(config: AuthConfig): Date {
  const seconds = parseExpiration(config.refreshExpires)
  return new Date(Date.now() + seconds * 1000)
}

export async function generateTokenPair(
  payload: JwtPayload,
  config: AuthConfig,
  user?: Record<string, unknown>
): Promise<TokenPair> {
  return {
    accessToken: await generateAccessToken(payload, config, user),
    refreshToken: generateRefreshToken()
  }
}

export function verifyAccessToken(token: string, secret: string): JwtPayload {
  return jwt.verify(token, secret) as JwtPayload
}
